import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import "./endofday.css";

/*
 * End of Day Closing – React port of end_of_day.php
 * GET /api/end-of-day for the summary, POST /api/end-of-day to close and email the report (backend/endofday.py).
 * Same rules as PHP: variance = actual count − expected cash; the confirm box must be ticked.
 */

const n0 = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function EndOfDay({ user }) {
  const username = user?.name || "User";
  const api = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { tone, text }

  const load = useCallback(async () => {
    try {
      setData(await api.get("/api/end-of-day"));
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const denoms = useMemo(() => data?.denominations || [], [data]);
  const actual = useMemo(
    () => denoms.reduce((sum, d) => sum + d * (parseInt(counts[d], 10) || 0), 0),
    [denoms, counts]
  );
  const expected = data?.expected_cash || 0;
  const variance = actual - expected;
  const varianceTone = variance > 0 ? "surplus" : variance < 0 ? "shortage" : "neutral";

  const submit = async (e) => {
    e.preventDefault();
    if (!confirmed) { setResult({ tone: "err", text: "Please confirm that all counts are accurate before closing the day." }); return; }
    setSubmitting(true);
    setResult(null);
    const payload = {
      total_transactions: data.summary.total_count,
      total_sales: data.summary.total_sales,
      total_expenses: data.summary.total_expenses,
      expected_cash: expected,
      actual_cash_count: actual,
      notes,
      confirm: "1",
    };
    denoms.forEach((d) => { payload[`count_${d}`] = parseInt(counts[d], 10) || 0; });
    try {
      const res = await api.post("/api/end-of-day", payload);
      setResult({ tone: "ok", text: res.message || "Day closed and report sent." });
      load();
    } catch (err) {
      setResult({ tone: "err", text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const cashier = (
    <div className="eod-cashier">
      <span className="eod-cashier-label">Cashier</span>
      <span className="eod-cashier-row"><span className="avatar">{username.trim().charAt(0).toUpperCase()}</span>{username}</span>
    </div>
  );

  const longDate = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <main className="pg eod">
      <PageHeader slug="end_of_day" actions={cashier} />
      <p className="eod-date"><i className="fas fa-calendar" /> {longDate}</p>

      {loadError && (
        <div className="eod-msg err" role="alert">
          Couldn't load today's figures: {loadError}
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      )}
      {!data && !loadError && <div className="panel eod-step"><div className="skel" style={{ height: 160 }} /></div>}

      {result?.tone === "ok" && <div className="eod-msg ok" role="status">{result.text}</div>}
      {data?.already_closed && (
        <div className="eod-closed">
          <span className="eod-closed-ic"><i className="fas fa-circle-check" /></span>
          <div>
            <h2>Day already closed</h2>
            <p>The end of day has already been processed for today.</p>
          </div>
        </div>
      )}

      {data && !data.already_closed && (
        <form onSubmit={submit}>
          {/* Step 1 */}
          <section className="panel eod-step" aria-labelledby="s1">
            <div className="eod-step-head"><span className="eod-badge">1</span><h2 id="s1">Transaction summary</h2></div>
            <div className="eod-metrics">
              <div className="eod-metric">
                <span className="eod-metric-label"><i className="fas fa-receipt" /> Transactions</span>
                <span className="eod-metric-value mono">{n0(data.summary.total_count)}</span>
                <span className="eod-metric-cap">Cash payments today</span>
              </div>
              <div className="eod-metric">
                <span className="eod-metric-label"><i className="fas fa-arrow-trend-up" /> Cash in</span>
                <span className="eod-metric-value mono">{n0(data.summary.total_sales)}</span>
                <span className="eod-metric-cap">TZS</span>
              </div>
              <div className="eod-metric">
                <span className="eod-metric-label"><i className="fas fa-briefcase" /> Expected</span>
                <span className="eod-metric-value mono">{n0(expected)}</span>
                <span className="eod-metric-cap">TZS</span>
              </div>
            </div>
            <div className="eod-opening">
              <span><i className="fas fa-rotate-left" /> Opening balance</span>
              <span className="mono">TZS {n0(data.opening_cash)}</span>
            </div>
          </section>

          {/* Step 2 */}
          <section className="panel eod-step" aria-labelledby="s2">
            <div className="eod-step-head"><span className="eod-badge">2</span><h2 id="s2">Count physical cash</h2></div>
            <div className="eod-denoms">
              {denoms.map((d) => {
                const isNote = d >= 500;
                const count = parseInt(counts[d], 10) || 0;
                return (
                  <div key={d} className="eod-denom">
                    <label htmlFor={`count_${d}`} className="eod-denom-head">
                      <span className="mono eod-denom-value">{n0(d)}</span>
                      <span className="eod-denom-kind">{isNote ? "Note" : "Coin"}</span>
                    </label>
                    <input id={`count_${d}`} type="number" min="0" inputMode="numeric" placeholder="0"
                      value={counts[d] ?? ""} onChange={(e) => setCounts((c) => ({ ...c, [d]: e.target.value }))}
                      onFocus={(e) => e.target.select()} />
                    <span className="eod-denom-total mono">{n0(count * d)}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Step 3 */}
          <section className="panel eod-step" aria-labelledby="s3">
            <div className="eod-step-head"><span className="eod-badge">3</span><h2 id="s3">Review and confirm</h2></div>
            <div className="eod-review">
              <div className="eod-rev expected"><span>Expected cash</span><strong className="mono">TZS {n0(expected)}</strong></div>
              <div className="eod-rev actual"><span>Actual count</span><strong className="mono">TZS {n0(actual)}</strong></div>
              <div className={`eod-rev ${varianceTone}`} aria-live="polite">
                <span>Variance{variance > 0 ? " (surplus)" : variance < 0 ? " (shortage)" : ""}</span>
                <strong className="mono">TZS {n0(variance)}</strong>
              </div>
            </div>

            <div className="field" style={{ marginBottom: "1.25rem" }}>
              <label htmlFor="eod-notes">Notes (optional)</label>
              <textarea id="eod-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about discrepancies, issues or observations" />
            </div>

            <label className="eod-confirm">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} required />
              <span>I confirm that all cash counts are accurate and I have carefully reviewed today's transactions. I understand this action will close the business day.</span>
            </label>

            {result?.tone === "err" && <div className={`eod-msg ${result.tone}`} role={result.tone === "err" ? "alert" : "status"}>{result.text}</div>}

            <div className="eod-actions">
              <button type="submit" className="btn primary eod-submit" disabled={submitting}>
                {submitting ? <><i className="fas fa-spinner fa-spin" /> Processing…</> : <><i className="fas fa-lock" /> Close day and send report</>}
              </button>
              <button type="button" className="btn" onClick={() => navigate("/dashboard")}>Cancel</button>
            </div>
          </section>
        </form>
      )}
    </main>
  );
}
